import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ExternalLink,
  Pencil,
  Trash2,
  Plus,
  Upload,
  Link as LinkIcon,
  Save,
  X as XIcon,
  Loader2,
  ImagePlus,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const MAX_LOGO_BYTES = 500 * 1024; // 500 KB

function normalizeUrl(url) {
  const u = (url || "").trim();
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return "https://" + u;
}

function isValidUrl(url) {
  const u = normalizeUrl(url);
  if (!u) return false;
  try {
    const parsed = new URL(u);
    return (
      (parsed.protocol === "http:" || parsed.protocol === "https:") &&
      parsed.hostname.includes(".")
    );
  } catch {
    return false;
  }
}

function faviconFor(url) {
  const u = normalizeUrl(url);
  if (!u) return null;
  try {
    const host = new URL(u).hostname;
    return `https://www.google.com/s2/favicons?sz=128&domain=${encodeURIComponent(host)}`;
  } catch {
    return null;
  }
}

function displayHostname(url) {
  const u = normalizeUrl(url);
  if (!u) return "";
  try {
    const p = new URL(u);
    return p.hostname + (p.pathname && p.pathname !== "/" ? p.pathname : "");
  } catch {
    return u;
  }
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("read error"));
    reader.readAsDataURL(file);
  });
}

function LogoDisplay({ logo, url, name, size = "card", onClick }) {
  const wrapperClasses =
    size === "card"
      ? "h-[90px] w-full max-w-[160px] mx-auto flex items-center justify-center"
      : "h-16 w-16 flex items-center justify-center";
  const imgClasses =
    size === "card"
      ? "max-h-[90px] max-w-[140px] object-contain"
      : "max-h-16 max-w-16 object-contain";

  const [fallbackFailed, setFallbackFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const src = !logoFailed && logo ? logo : (!fallbackFailed ? faviconFor(url) : null);

  const clickable = !!onClick;
  const Wrap = clickable ? "button" : "div";
  return (
    <Wrap
      type={clickable ? "button" : undefined}
      onClick={onClick}
      className={
        wrapperClasses +
        (clickable
          ? " cursor-pointer hover:scale-105 transition-transform"
          : "")
      }
      title={clickable ? "Abrir enlace en nueva pestaña" : undefined}
      aria-label={clickable ? `Abrir ${name}` : undefined}
    >
      {src ? (
        <img
          src={src}
          alt={name || "logo"}
          className={imgClasses}
          onError={() => {
            if (!logoFailed && logo) setLogoFailed(true);
            else setFallbackFailed(true);
          }}
        />
      ) : (
        <div className="h-14 w-14 rounded-xl bg-neutral-100 border border-neutral-200 flex items-center justify-center text-neutral-400">
          <ImagePlus className="h-6 w-6" />
        </div>
      )}
    </Wrap>
  );
}

function InstitutionForm({ initial, onCancel, onSubmit, submitting }) {
  const [name, setName] = useState(initial?.name || "");
  const [url, setUrl] = useState(initial?.url || "");
  const [logoB64, setLogoB64] = useState(initial?.logo_b64 || null);
  const [urlError, setUrlError] = useState("");
  const [logoError, setLogoError] = useState("");
  const fileRef = useRef(null);

  const handleFile = useCallback(async (e) => {
    setLogoError("");
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setLogoError("El archivo debe ser una imagen.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError(`El logo no puede superar los ${Math.round(MAX_LOGO_BYTES / 1024)} KB.`);
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setLogoB64(dataUrl);
    } catch {
      setLogoError("No se pudo leer el archivo.");
    }
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setUrlError("");
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Introduce un nombre para la institución.");
      return;
    }
    if (!isValidUrl(url)) {
      setUrlError("Introduce una URL válida (por ejemplo: dgt.es o https://ejemplo.com).");
      return;
    }
    onSubmit({
      name: trimmedName,
      url: normalizeUrl(url),
      logo_b64: logoB64 || null,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="institution-form">
      <div>
        <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Logo</label>
        <div className="mt-2 flex items-center gap-3">
          <div className="h-20 w-20 rounded-xl border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden shrink-0">
            {logoB64 ? (
              <img
                src={logoB64}
                alt="preview"
                className="max-h-16 max-w-16 object-contain"
                data-testid="institution-form-logo-preview"
              />
            ) : (
              <ImagePlus className="h-6 w-6 text-neutral-400" />
            )}
          </div>
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="btn-secondary !h-9 !px-3 !text-xs"
              data-testid="institution-form-upload"
            >
              <Upload className="h-3.5 w-3.5" />
              {logoB64 ? "Cambiar logo" : "Subir logo"}
            </button>
            {logoB64 && (
              <button
                type="button"
                onClick={() => setLogoB64(null)}
                className="text-xs text-neutral-500 hover:text-red-600 text-left"
                data-testid="institution-form-remove-logo"
              >
                Quitar logo
              </button>
            )}
            <span className="text-[10px] text-neutral-400">PNG/JPG · máx. 500 KB</span>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="hidden"
            data-testid="institution-form-file"
          />
        </div>
        {logoError && <p className="text-xs text-red-600 mt-1">{logoError}</p>}
      </div>

      <div>
        <label htmlFor="inst-name" className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Nombre</label>
        <input
          id="inst-name"
          type="text"
          value={name}
          maxLength={120}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ej. DGT — Sede Electrónica"
          className="input-base mt-1"
          data-testid="institution-form-name"
          required
        />
      </div>

      <div>
        <label htmlFor="inst-url" className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">URL</label>
        <input
          id="inst-url"
          type="text"
          value={url}
          maxLength={1000}
          onChange={(e) => { setUrl(e.target.value); setUrlError(""); }}
          placeholder="https://sede.dgt.gob.es"
          className={"input-base mt-1 " + (urlError ? "border-red-500 focus:ring-red-200" : "")}
          data-testid="institution-form-url"
          required
        />
        {urlError && <p className="text-xs text-red-600 mt-1" data-testid="institution-form-url-error">{urlError}</p>}
        {!urlError && url && (
          <p className="text-[11px] text-neutral-500 mt-1 truncate">
            Se abrirá: <span className="font-mono">{normalizeUrl(url)}</span>
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="btn-ghost !h-9 !px-3 !text-sm border border-neutral-200"
          data-testid="institution-form-cancel"
        >
          <XIcon className="h-3.5 w-3.5" /> Cancelar
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary !h-9 !px-4 !text-sm"
          data-testid="institution-form-submit"
        >
          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {initial ? "Guardar cambios" : "Crear institución"}
        </button>
      </div>
    </form>
  );
}

function InstitutionCard({ inst, onOpen, onEdit, onDelete }) {
  const disabled = !inst.url || !isValidUrl(inst.url);
  const host = useMemo(() => displayHostname(inst.url), [inst.url]);
  return (
    <article
      data-testid={`institution-card-${inst.id}`}
      className="w-full max-w-[320px] mx-auto sm:mx-0 rounded-2xl border border-neutral-200 bg-white shadow-sm hover:shadow-md hover:border-orange-300 transition-all flex flex-col overflow-hidden"
    >
      <div className="p-5 pb-3">
        <LogoDisplay
          logo={inst.logo_b64}
          url={inst.url}
          name={inst.name}
          size="card"
          onClick={disabled ? undefined : () => onOpen(inst)}
        />
      </div>
      <div className="px-5 pb-4 text-center">
        <h3
          className="font-bold text-base text-neutral-900 truncate"
          title={inst.name}
          data-testid={`institution-name-${inst.id}`}
        >
          {inst.name}
        </h3>
        {host && (
          <p
            className="text-xs text-neutral-500 truncate font-mono mt-0.5"
            title={inst.url}
            data-testid={`institution-url-${inst.id}`}
          >
            {host}
          </p>
        )}
      </div>
      <div className="px-4 pb-4 flex flex-col gap-2 mt-auto">
        <button
          type="button"
          onClick={() => onOpen(inst)}
          disabled={disabled}
          className="btn-primary !h-9 !text-sm w-full disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`institution-open-${inst.id}`}
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Ir al enlace
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onEdit(inst)}
            className="btn-secondary !h-8 !text-xs flex-1"
            data-testid={`institution-edit-${inst.id}`}
          >
            <Pencil className="h-3 w-3" /> Editar
          </button>
          <button
            type="button"
            onClick={() => onDelete(inst)}
            className="btn-ghost !h-8 !text-xs flex-1 border border-neutral-200 text-red-600 hover:bg-red-50 hover:border-red-200"
            data-testid={`institution-delete-${inst.id}`}
          >
            <Trash2 className="h-3 w-3" /> Eliminar
          </button>
        </div>
      </div>
    </article>
  );
}

export default function LinksTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=create, obj=edit
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institutions");
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudieron cargar los enlaces");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openLink = useCallback((inst) => {
    const url = normalizeUrl(inst.url);
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const startCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const startEdit = (inst) => {
    setEditing(inst);
    setDialogOpen(true);
  };

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      if (editing) {
        await api.patch(`/institutions/${editing.id}`, payload);
        toast.success("Institución actualizada");
      } else {
        await api.post("/institutions", payload);
        toast.success("Institución creada");
      }
      setDialogOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/institutions/${confirmDelete.id}`);
      toast.success("Institución eliminada");
      setConfirmDelete(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error al eliminar");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">Enlaces institucionales</h2>
          <p className="text-sm text-neutral-500 mt-0.5">
            Accesos rápidos a las webs que utilizas cada día.
          </p>
        </div>
        <button
          type="button"
          onClick={startCreate}
          className="btn-primary !h-10 !px-4 !text-sm"
          data-testid="institution-add"
        >
          <Plus className="h-4 w-4" />
          Añadir institución
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 py-6 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="institution-empty"
          className="rounded-2xl border-2 border-dashed border-neutral-200 bg-white/50 p-10 text-center"
        >
          <LinkIcon className="h-8 w-8 mx-auto text-neutral-300" />
          <h3 className="mt-3 font-semibold text-neutral-800">Aún no hay enlaces</h3>
          <p className="text-sm text-neutral-500 mt-1">
            Añade la primera institución para tenerla siempre a un clic.
          </p>
          <button
            type="button"
            onClick={startCreate}
            className="btn-primary !h-9 !px-4 !text-sm mt-4"
            data-testid="institution-add-empty"
          >
            <Plus className="h-3.5 w-3.5" /> Añadir institución
          </button>
        </div>
      ) : (
        <div
          data-testid="institution-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
        >
          {items.map((inst) => (
            <InstitutionCard
              key={inst.id}
              inst={inst}
              onOpen={openLink}
              onEdit={startEdit}
              onDelete={setConfirmDelete}
            />
          ))}
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}
      >
        <DialogContent className="max-w-md" data-testid="institution-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar institución" : "Nueva institución"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Modifica el logo, nombre o URL de la institución."
                : "Añade una institución para tenerla siempre accesible con un clic."}
            </DialogDescription>
          </DialogHeader>
          <InstitutionForm
            initial={editing}
            onCancel={() => { setDialogOpen(false); setEditing(null); }}
            onSubmit={handleSubmit}
            submitting={submitting}
          />
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="institution-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar “{confirmDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. La institución y su logo se eliminarán permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="institution-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="institution-delete-confirm"
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
